package com.dotdrives.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlin.time.Instant

/** Raw `reminders` row shape (docs/schema.md, "## Reminders / steps and automation rules"). */
@Serializable
data class Reminder(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("subject_type")
    val subjectType: String,
    @SerialName("subject_id")
    val subjectId: String,
    val description: String,
    @SerialName("assigned_to_user_id")
    val assignedToUserId: String,
    @SerialName("due_at")
    val dueAt: Instant,
    @SerialName("completed_at")
    val completedAt: Instant?,
    @SerialName("is_priority")
    val isPriority: Boolean,
    val recurrence: String?,
    @SerialName("created_by_user_id")
    val createdByUserId: String?,
    @SerialName("created_by_rule_id")
    val createdByRuleId: String?,
    // Idempotency marker for the nightly send-reminders job (ADR 0007). Never a UI flag --
    // exposed on the model only because it round-trips through the row; the presentation
    // layer must not read it (docs/schema.md).
    @SerialName("emailed_at")
    val emailedAt: Instant?,
    @SerialName("created_at")
    val createdAt: Instant,
    @SerialName("email_enabled")
    val emailEnabled: Boolean = true,
)

enum class ReminderFilter {
    All,
    Mine,
    AutoCreated,
    Recurring,
    Done,
}

/** One reminder-list-row worth of data, enriched with subject/assignee/rule display text. */
data class ReminderSummary(
    val id: String,
    val subjectType: String,
    val subjectId: String,
    val subjectLabel: String,
    val subjectContext: String,
    val description: String,
    val assignedToUserId: String,
    val assignedToName: String?,
    val dueAt: Instant,
    val completedAt: Instant?,
    val isPriority: Boolean,
    val recurrence: String?,
    val createdByRuleId: String?,
    val ruleName: String?,
    val emailEnabled: Boolean = true,
) {
    /** The "⚡ AUTO" flag — set when an automation rule created this reminder. */
    val isAuto: Boolean get() = createdByRuleId != null
}

data class NewReminder(
    val description: String,
    val subjectType: String,
    val subjectId: String,
    val assignedToUserId: String,
    val dueAt: Instant,
    val recurrence: String?,
    val emailEnabled: Boolean = true,
)

/** A giver/drive/report a new reminder can be linked to — the "Linked to" picker's result row. */
data class ReminderSubjectOption(
    val subjectType: String,
    val subjectId: String,
    val label: String,
    val context: String,
)

/**
 * Raw `automation_rules` row shape. `triggerVerb == null` means "cron-scanned, not
 * event-dispatched" -- the two built-in condition rules (stale-drive nudge, lapsed-donor
 * alert) carry null; this is not an error state and must never render as one (docs/schema.md).
 */
@Serializable
data class AutomationRule(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val name: String,
    @SerialName("is_builtin")
    val isBuiltin: Boolean,
    @SerialName("is_enabled")
    val isEnabled: Boolean,
    @SerialName("trigger_verb")
    val triggerVerb: String?,
    @SerialName("trigger_conditions")
    val triggerConditions: JsonObject,
    @SerialName("action_type")
    val actionType: String,
    @SerialName("action_config")
    val actionConfig: JsonObject,
    @SerialName("created_at")
    val createdAt: Instant,
    @SerialName("updated_at")
    val updatedAt: Instant,
)

data class NewAutomationRule(
    val name: String,
    val triggerVerb: String?,
    val minAmountCents: Long?,
    val assignee: String,
    val descriptionTemplate: String,
    val dueInDays: Int,
)

/**
 * The rule builder's "would have fired" dry run. `complete = false` when the min-amount join
 * to `donations` returned fewer rows than the matching events named -- a `field`-role viewer
 * (RLS via `has_perm_strict('donation.read')`) reaching this preview would otherwise see a
 * confident "0" that is actually "can't tell," not a real zero.
 */
data class RulePreviewCount(val count: Int, val complete: Boolean)

/** One "Drive idle 2x phase median" row for the pipeline-health rail. */
data class DriveHealthEntry(
    val driveId: String,
    val driveName: String,
    val phaseId: String,
    val phaseName: String,
    val daysInPhase: Long,
    val medianDays: Double,
    val suggestion: String,
)
